import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { z } from 'zod'
import { kUserFilesApiBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { formatUserFilesErrorDetail } from '@/user_files/utils/api-error'

export const UserFileViewableUrlResponseSchema = z.object({
  file_id: z.string(),
  viewable_url: z.string().min(1),
  expires_at: z.string(),
  content_type: z.string().min(1),
})

export type UserFileViewableUrlResponse = z.infer<typeof UserFileViewableUrlResponseSchema>

export async function getUserFileViewableUrl(fileId: string): Promise<UserFileViewableUrlResponse> {
  const sentryTransactionId = nanoid()
  const auth = getAuth()
  const token = await auth.currentUser?.getIdToken()

  if (!token) {
    throw new Error('User not authenticated')
  }

  const url = `${kPaxtonAppApiRESTBaseUrl()}${kUserFilesApiBasePath}/${encodeURIComponent(fileId)}/viewable-url`
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Transaction-Id': sentryTransactionId,
    },
  })

  const { data, parseError } = await readResponseJson(response)
  if (!response.ok) {
    const errorData = parseError ? {} : data
    const detail = formatUserFilesErrorDetail(errorData)
    throw new ApiError(response.status, detail || `Failed to create viewable URL: ${response.status}`)
  }

  if (parseError) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: sentryTransactionId })
      Sentry.captureException(new Error('User file viewable URL response JSON parse failed'), {
        extra: {
          ...getResponseDebugContext(response, url),
          fileId,
          parseError: formatUnknownError(parseError),
        },
      })
    })

    throw new ApiResponsePayloadError(response.status, 'Invalid user file viewable URL response JSON')
  }

  const parsed = UserFileViewableUrlResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: sentryTransactionId })
      Sentry.captureException(new Error('User file viewable URL response schema validation failed'), {
        extra: {
          ...getResponseDebugContext(response, url),
          fileId,
          responseKeys: getTopLevelKeys(data),
          issues: parsed.error.issues,
        },
      })
    })

    throw new ApiResponsePayloadError(response.status, 'Invalid user file viewable URL response shape', parsed.error.issues)
  }

  return parsed.data
}
